import React from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import licensedIcon from '../../../assets/icons/features/licensed.webp';
import warrantyIcon from '../../../assets/icons/features/warranty.webp';
import transparentIcon from '../../../assets/icons/features/transparent.webp';
import mobileIcon from '../../../assets/icons/features/mobile.webp';

/**
 * Trust Section - Replaces fake stats with trust badges
 */
const TrustSection = () => {
  const theme = useTheme();

  // Order: 1st Licensed, 2nd Warranty, 3rd Transparent, 4th (last) Mobile
  const trustBadges = [
    { imageSrc: licensedIcon, title: 'Licensed Professionals', description: 'Certified technicians you can trust' },
    { imageSrc: warrantyIcon, title: 'Warranty Protected', description: 'Work backed by our guarantee', detail: '12,000 miles or 12 months' },
    { imageSrc: transparentIcon, title: 'Transparent Pricing', description: 'Upfront estimates, no surprises' },
    { imageSrc: mobileIcon, title: 'Mobile Convenience', description: 'We come to you, save your time' }
  ];

  return (
    <Box
      sx={{
        background: theme.palette.background.paper,
        py: { xs: 4, sm: 6 },
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              lineHeight: 1.6,
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            Professional service without the shop visit.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: { xs: 1.5, sm: 2, md: 3 },
            alignItems: 'stretch'
          }}
        >
          {trustBadges.map((badge, index) => (
            <Box
              key={index}
              sx={{
                textAlign: 'center',
                p: { xs: 1.5, sm: 2, md: 3 },
                borderRadius: 2,
                background: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%',
                '@media (hover: hover)': {
                  transition: 'all 0.25s ease-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
                    borderColor: theme.palette.primary.main
                  }
                }
              }}
            >
              <Box
                component="img"
                src={badge.imageSrc}
                alt=""
                loading="lazy"
                sx={{
                  width: { xs: 72, sm: 88, md: 128 },
                  height: { xs: 72, sm: 88, md: 128 },
                  objectFit: 'contain',
                  mb: { xs: 1, sm: 1.5, md: 2 }
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  fontSize: { xs: '0.8125rem', sm: '1rem', md: '1.125rem' },
                  lineHeight: 1.25
                }}
              >
                {badge.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.6875rem', sm: '0.8125rem', md: '0.875rem' },
                  lineHeight: 1.4
                }}
              >
                {badge.description}
              </Typography>
              {badge.detail && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.625rem', sm: '0.75rem', md: '0.8125rem' },
                    lineHeight: 1.35,
                    mt: 0.25,
                    opacity: 0.9
                  }}
                >
                  {badge.detail}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default TrustSection;
